// Created by jarongallo at 3/31/21
import React, { useEffect, useState } from 'react';
import './styles';
import { getInitialRoute } from '../../../../../utils/Helpers/UserHelpers';
import PLEditImage from '../../../../../components/PL/EditImage';
import UIIconButton from '../../../../../components/UI/UIIconButton';
import UIListInputRow from '../../../../../components/UI/UIListInputRow';
import UILabeledInput from '../../../../../components/UI/UILabeledInput';
import UIAdditionalContacts from '../../../../../components/UI/UITeamMembers';
import { PlusIcon } from '../../../../../assets/icons';
import Queries from '../../../../../utils/API/Queries';
import UIModal from '../../../../../components/UI/UIModal';
import UIOverlayCard from '../../../../../components/UI/UIOverlayCard';
import AdditionalContacts from '../../../../../components/modals/AddTeamMemberModal';
import { UI_LABELED_INPUT_PHONE_NUMBER_TYPE, UI_LABELED_INPUT_TEXT_TYPE } from '../../../../../components/UI/UILabeledInput/config';
import { CHILD_TYPE } from '../../../../../components/UI/UIListInputRow/config';
import { useApolloClient } from 'react-apollo';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { GET_AGENT_INFO } from './queries';
import { toast } from 'react-toastify';
import * as strings from './strings';
import { camelCaseToSentence } from '../../../../../utils/Helpers/StringHelpers';
import { useDispatch } from 'react-redux';
import { setSignedInUser } from '../../../../../redux/store/user/actions';
import { useHistory, useLocation } from 'react-router-dom';
import { UI_ICON_BUTTON_CANCEL_TYPE, UI_ICON_BUTTON_FILL_TYPE } from '../../../../../components/UI/UIIconButton/config';

export default function IndividualAgentInfo({ agentId }: IndividualAgentInfoProps) {
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();

	const [agent, setAgent] = useState<UserType>();

	const [changingPassword, setChangePassword] = useState<boolean>(false);
	const [addingAdditionalContacts, setAddingAdditionalContacts] = useState<boolean>(false);
	const [deletingAdditionalContacts, setDeletingAdditionalContacts] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const [password, setPassword] = useState<string>('');
	const [confirmPassword, setConfirmPassword] = useState<string>('');

	const [name, setName] = useState<string>();
	const [email, setEmail] = useState<string>();
	const [phoneNumber, setPhoneNumber] = useState<string>();
	const [notes, setNotes] = useState<string>();

	const [contactToDelete, setContactToDelete] = useState<AdditionalContact>();

	const client = useApolloClient();

	const [updateAgentApi] = useMutation(Queries.UPDATE_USER_MUTATION, {
		onError: (error) => toast.error(strings.errorUpdatingUser(error.message))
	});

	const [getAgent, { data: agentData }] = useLazyQuery(GET_AGENT_INFO, {
		variables: { id: agentId }
	});

	useEffect(() => {
		(async () => {
			await getAgent();
		})();
	}, []);

	useEffect(() => {
		(() => {
			if (agentData) {
				const fetchedAgent: UserType = agentData['user'];
				setAgent(fetchedAgent);
				setName(fetchedAgent.name);
				setEmail(fetchedAgent.email);
				setPhoneNumber(fetchedAgent.phoneNumber);
				setNotes(fetchedAgent.notes);
			}
		})();
	}, [agentData]);

	if (!agent) return null;

	const updateAgent = async (key: string, value: any) => {
		await updateAgentApi({ variables: { id: agentId, [key]: value } });
		toast.success(strings.updateUserSuccess(camelCaseToSentence(key)));
	};

	const addAdditionalContact = async (contact) => {
		let additionalContacts = agent.additionalContacts;

		let variables;
		if (additionalContacts) {
			variables = {
				id: agentId,
				additionalContacts: [...agent.additionalContacts, contact]
			};
		} else {
			variables = {
				id: agentId,
				additionalContacts: [contact]
			};
		}

		const response = await client.mutate({
			mutation: Queries.UPDATE_USER_MUTATION,
			variables: variables
		});
		const { updateUser } = response['data'];
		if (updateUser) {
			await getAgent();
			toast.success('Updated additional contacts!');
		}
	};

	const deleteAdditionalContact = async (contactToDelete) => {
		const additionalAgentContacts = agent.additionalContacts;
		const result = additionalAgentContacts.filter((contact) => contact.name !== contactToDelete.name);
		const response = await client.mutate({
			mutation: Queries.UPDATE_USER_MUTATION,
			variables: { id: agentId, additionalContacts: result }
		});
		const { updateUser } = response['data'];

		if (updateUser) {
			await getAgent();
			toast.success('Deleted additional contact!');
		}
	};

	const updateAgentInfo = async (key, value) => {
		setIsLoading(true);
		await updateAgent(key, value);
		await getAgent();
		setIsLoading(false);
	};

	const renderChangePasswordModal = () => {
		const changePassword = async () => {
			if (!isLoading) {
				if (password.trim().length < 8) {
					toast.error('Password must be at least 8 character!');
				} else if (password.trim() !== confirmPassword.trim()) {
					toast.error('Passwords do not match');
				} else {
					setIsLoading(true);

					const response = await client
						.mutate({
							mutation: Queries.CHANGE_PASSWORD,
							variables: { id: agentId, password }
						})
						.catch((error) => {
							setIsLoading(false);
							toast.error('Error updating your password. Please call us!! ' + error.message);
						});
					if (response['data']) {
						await getAgent();
						setChangePassword(false);
						setPassword('');
						setConfirmPassword('');
						setIsLoading(false);
						toast.success('Successfully updated password!');
					} else {
						setPassword('');
						setConfirmPassword('');
						setIsLoading(false);
						toast.error('Error updating your password. Please call us!');
					}
				}
			}
		};
		const hideModal = () => {
			setChangePassword(false);
		};

		return (
			<UIModal condition={changingPassword}>
				<UIOverlayCard hideModal={hideModal} title={'Change password'}>
					<UILabeledInput
						containerStyle={{ width: '100%' }}
						style={{ width: '100%' }}
						onChangeValue={(password) => setPassword(password)}
						label={'PASSWORD'}
						value={password}
						type={'text'}
						placeholder={'Password...'}
					/>
					<UILabeledInput
						containerStyle={{ marginTop: 8, width: '100%' }}
						style={{ width: '100%' }}
						onChangeValue={(confirmPassword) => setConfirmPassword(confirmPassword)}
						label={'CONFIRM PASSWORD'}
						value={confirmPassword}
						type={'text'}
						placeholder={'Confirm password...'}
					/>
					<div className='general-buttons-container'>
						<UIIconButton dark isLoading={isLoading} type={UI_ICON_BUTTON_FILL_TYPE} text={'update password'} onClick={changePassword} />
					</div>
				</UIOverlayCard>
			</UIModal>
		);
	};
	const renderAdditionalContactsModal = () => {
		const hideModal = () => setAddingAdditionalContacts(false);
		return (
			<UIModal condition={addingAdditionalContacts}>
				<AdditionalContacts
					//@ts-ignore
					hideModal={hideModal}
					onAddTeamMember={async (contact) => {
						await addAdditionalContact(contact);
						hideModal();
					}}
				/>
			</UIModal>
		);
	};
	const renderDeleteContactModal = () => {
		if (!contactToDelete) {
			return null;
		}

		const hideModal = () => {
			setDeletingAdditionalContacts(false);
			setIsLoading(false);
		};
		const deleteContactOnAgent = async () => {
			setIsLoading(true);
			await deleteAdditionalContact(contactToDelete);
			hideModal();
		};
		return (
			<UIModal condition={deletingAdditionalContacts}>
				<UIOverlayCard
					hideModal={hideModal}
					title={'Remove team member'}
					description={`Are you sure you want to remove ${contactToDelete.name} as a team member?`}>
					<div className='deleteBtnContainer'>
						<UIIconButton
							dark
							isLoading={isLoading}
							type={UI_ICON_BUTTON_CANCEL_TYPE}
							onClick={deleteContactOnAgent}
							text={'remove'}
							className='deleteBtnContainer__btn'
						/>
					</div>
				</UIOverlayCard>
			</UIModal>
		);
	};
	const deleteContact = (contact) => {
		setContactToDelete(contact);
		setDeletingAdditionalContacts(true);
	};
	return (
		<div className='agentPage'>
			<div className='individualAgent__card--info'>
				<div className='individualAgent__card--info-left'>
					<PLEditImage
						imageStyle={{ width: 128, height: 128, minWidth: 128, minHeight: 128, margin: '0 32px 16px 32px' }}
						updateUser={getAgent}
						userId={agent.id}
						userName={agent.name}
						userImage={agent.image}
					/>
					<div>
						<UIIconButton
							onClick={() => updateAgentInfo('isActive', !agent.isActive)}
							text={agent.isActive ? 'deactivate' : 're-activate'}
							type={agent.isActive ? UI_ICON_BUTTON_CANCEL_TYPE : UI_ICON_BUTTON_CANCEL_TYPE}
						/>
						<p className='individual-agent-password-change' onClick={() => setChangePassword(true)}>
							change password
						</p>
						<p className='individual-agent-password-change-required' onClick={() => updateAgent('requiresPasswordChange', true)}>
							require password change
						</p>
					</div>
				</div>
				<p
					className='individualAgent--viewAs'
					onClick={() => {
						dispatch(setSignedInUser(agent));
						history.push(getInitialRoute(agent) + location.search);
					}}>
					view as agent
				</p>

				<UIListInputRow
					className='individualAgent__card--info-right--label-full'
					style={{ width: '100%' }}
					type={UI_LABELED_INPUT_TEXT_TYPE}
					savedValue={agent.name}
					onSave={() => updateAgentInfo('name', name)}
					onChangeValue={setName}
					value={name}
					isLoading={isLoading}
					placeholder={'agent name...'}
				/>
				<UIListInputRow
					className='individualAgent__card--info-right--label-full'
					style={{ width: '100%' }}
					type={UI_LABELED_INPUT_TEXT_TYPE}
					savedValue={agent.email}
					onSave={() => updateAgentInfo('email', email)}
					onChangeValue={setEmail}
					value={email}
					isLoading={isLoading}
					placeholder={'agent email...'}
				/>
				<UIListInputRow
					className='individualAgent__card--info-right--label-half'
					type={UI_LABELED_INPUT_PHONE_NUMBER_TYPE}
					savedValue={agent.phoneNumber}
					onSave={() => updateAgentInfo('phoneNumber', phoneNumber)}
					onChangeValue={setPhoneNumber}
					value={phoneNumber}
					isLoading={isLoading}
					placeholder={'agent phone number...'}
				/>
				<UIListInputRow
					className='individualAgent__card--info-right--label-full'
					label={'AGENT PREFERENCES'}
					type={'textField'}
					savedValue={agent.notes}
					onSave={() => updateAgentInfo('notes', notes)}
					onChangeValue={setNotes}
					value={notes}
					isLoading={isLoading}
					placeholder={'Agent preferences...'}
				/>
			</div>
			<div className='individualAgent__card--contacts'>
				<UIListInputRow type={CHILD_TYPE} className='editAdditionalContacts'>
					<p className='general-title'>Team</p>
					<p className='general-description' style={{ marginBottom: 8 }}>
						Team members will receive all notifications for all listings and services on this account.
					</p>
					<UIAdditionalContacts
						deleteOnClick={true}
						data={agent.additionalContacts}
						deleteAdditionalContact={deleteContact}
						addAdditionalContact={addAdditionalContact}
					/>
					<div className='additionalContacts'>
						<UIIconButton dark onClick={() => setAddingAdditionalContacts(true)} icon={PlusIcon.grey} text='add team member' />
					</div>
					<p className='general-overlay-sub-text'>*You can add a contact to a single listing from within the listing itself.</p>
				</UIListInputRow>
			</div>
			{renderAdditionalContactsModal()}
			{renderDeleteContactModal()}
			{renderChangePasswordModal()}
		</div>
	);
}
